import pluralize from 'pluralize';
import { memo, useCallback, useMemo, useState } from 'react';

import { ChevronDownIcon, MinusIcon } from '@air/design-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';
import { SelectionList } from '~/components/v2/components/shared/selectionBar/components/SelectionList';

export const SelectedItemListSummary = memo(() => {
  const { selectedItemCount } = useShowcaseModal();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  const itemsSelected = useMemo(() => pluralize('Item', selectedItemCount, true), [selectedItemCount]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
  }, []);

  const handleFocusOutside = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  const handleEscapeKeyDown = useCallback((event: KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  if (!selectedItemCount) {
    return null;
  }

  return (
    <DropdownMenu
      className="max-h-[400px] w-[400px] overflow-y-auto p-0"
      trigger={
        <div className="flex cursor-pointer items-center gap-1 text-16 text-gray-12">
          <span>{itemsSelected} selected</span>
          <IconButton adsIcon label="Open menu" appearance="ghost" color="gray" size="small" icon={ChevronDownIcon} />
        </div>
      }
      size="large"
      open={isMenuOpen}
      onOpenChange={handleOpenChange}
      onFocusOutside={handleFocusOutside}
      onEscapeKeyDown={handleEscapeKeyDown}
      disabled={selectedItemCount <= 1}
      // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
      ref={(newRef) => setScrollElement(newRef)}
    >
      <div className="flex select-none flex-col">
        <div className="sticky top-0 z-30 flex w-full items-center justify-between gap-2.5 bg-gray-1 px-3 pt-3 pb-3">
          <div className="ml-1 font-medium text-14 text-gray-12">{itemsSelected} selected</div>
          <IconButton
            adsIcon
            onClick={handleCloseMenu}
            label="Delete"
            appearance="ghost"
            color="gray"
            size="small"
            icon={MinusIcon}
          />
        </div>
        <SelectionList scrollElement={scrollElement} allowUnselect={false} />
      </div>
    </DropdownMenu>
  );
});

SelectedItemListSummary.displayName = 'SelectedItemListSummary';
